import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import { getAccessToken } from '../../../authUtils';
import { Modal, FormField, TextInput, Btn, ErrorBlock, COLORS, GRADIENTS, SHADOWS, Icons, SparkleBadge } from '../../ui/crm-ui';
import DocumentList from '../../DocumentList';

export interface PromptForGeneration {
  prompt_id: number;
  prompt_name: string;
  output_filename_prefix: string | null;
  upload_slot_1_label?: string | null;
  upload_slot_2_label?: string | null;
}

interface Props {
  baseApiUrl: string;
  open: boolean;
  prompt: PromptForGeneration | null;
  initialFolderId?: string;
  entityId?: number | null;
  jobId?: number | null;
  onClose: () => void;
  onSuccess: (result: { fileName: string; sharePointUrl: string | null }) => void;
  onSubmitted?: (dispatchId: number) => void;
}

interface GenerateResponse {
  success: boolean;
  fileName: string;
  sharePointUrl: string | null;
  tokensUsed: number;
  documentsProcessed: number;
}

interface DriveItem {
  id: string;
  name: string;
  type: 'folder' | 'file';
}

function apiError(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: string; raw?: string } | undefined;
    const msg = data?.error || err.message;
    return data?.raw ? `${msg}\n\nClaude output (first 500 chars):\n${data.raw}` : msg;
  }
  return err instanceof Error ? err.message : String(err);
}

const GenerateDocumentModal: React.FC<Props> = ({
  baseApiUrl, open, prompt, initialFolderId, entityId, jobId, onClose, onSuccess, onSubmitted,
}) => {
  const [filename, setFilename] = useState('');
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [folderId, setFolderId] = useState<string>(initialFolderId || 'root');
  const [folderName, setFolderName] = useState<string>('');
  const [folderHistory, setFolderHistory] = useState<Array<{ id: string; name: string }>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [statusStartTime, setStatusStartTime] = useState<Date | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFile1, setUploadedFile1] = useState<File | null>(null);
  const [uploadedFile2, setUploadedFile2] = useState<File | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [search, setSearch] = useState('');
  const [unindexedIds, setUnindexedIds] = useState<Set<string>>(new Set());
  const statusMessageIndexRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setFilename('');
    setSelectedDocIds([]);
    setFolderId(initialFolderId || 'root');
    setFolderName('');
    setFolderHistory([]);
    setSubmitting(false);
    setStatusMessage('');
    setStatusStartTime(null);
    setElapsedSeconds(0);
    setError(null);
    setSearch('');
    setUnindexedIds(new Set());
    statusMessageIndexRef.current = 0;
    setUploadedFile1(null);
    setUploadedFile2(null);
  }, [open, initialFolderId]);

  // Update elapsed seconds and cycle status messages every 5-10 seconds
  useEffect(() => {
    if (!submitting || !statusStartTime) return;

    const interval = setInterval(() => {
      const now = new Date();
      const elapsed = Math.floor((now.getTime() - statusStartTime.getTime()) / 1000);
      setElapsedSeconds(elapsed);

      // Update status message every 5-10 seconds (randomize to show variety)
      if (elapsed % 8 === 0 && elapsed > 0) {
        const messages = [
          'Processing your request…',
          'Claude is working on this…',
          'Analyzing documents…',
          'Building output…',
          'Almost done…',
        ];
        const nextIndex = statusMessageIndexRef.current % messages.length;
        setStatusMessage(messages[nextIndex]);
        statusMessageIndexRef.current += 1;
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [submitting, statusStartTime]);

  const isAtRoot = folderHistory.length === 0;

  function navigateInto(id: string, name: string) {
    setFolderHistory((h) => [...h, { id: folderId, name: folderName }]);
    setFolderId(id);
    setFolderName(name);
    setSearch('');
  }

  function navigateBack() {
    setFolderHistory((h) => {
      if (h.length === 0) return h;
      const next = [...h];
      const prev = next.pop()!;
      setFolderId(prev.id);
      setFolderName(prev.name);
      return next;
    });
  }

  const handleItemsLoaded = useCallback((items: DriveItem[]) => {
    const fileIds = items.filter((i) => i.type === 'file').map((i) => i.id);
    if (fileIds.length === 0) return;
    axios
      .post<{ indexed: string[] }>(`${baseApiUrl}/api/documents/vector-status`, { ids: fileIds })
      .then(({ data }) => {
        const indexedSet = new Set(data.indexed);
        const newUnindexed = fileIds.filter((id) => !indexedSet.has(id));
        if (newUnindexed.length > 0) {
          setUnindexedIds((prev) => {
            const next = new Set(prev);
            newUnindexed.forEach((id) => next.add(id));
            return next;
          });
        }
      })
      .catch(() => {
        // Status check failure is non-critical — just don't show indicators
      });
  }, [baseApiUrl]);

  async function handleSubmit() {
    if (!prompt) return;
    setError(null);
    if (selectedDocIds.length === 0) { setError('Select at least one source document.'); return; }
    if (!filename.trim()) { setError('Output filename is required.'); return; }

    setSubmitting(true);

    const docCount = selectedDocIds.length;
    const initialMessage = `Fetching metadata for ${docCount} document${docCount > 1 ? 's' : ''}…`;
    setStatusMessage(initialMessage);
    setStatusStartTime(new Date());
    setElapsedSeconds(0);
    statusMessageIndexRef.current = 0;

    // Budget Analysis: two Excel files uploaded from the user's machine.
    const needsUploads = !!prompt.upload_slot_1_label || !!prompt.upload_slot_2_label;
    if (prompt.upload_slot_1_label && !uploadedFile1) {
      setError(`Please upload: ${prompt.upload_slot_1_label}`);
      return;
    }
    if (prompt.upload_slot_2_label && !uploadedFile2) {
      setError(`Please upload: ${prompt.upload_slot_2_label}`);
      return;
    }
    let uploadedFile1Base64: string | undefined;
    let uploadedFile2Base64: string | undefined;
    const readAsBase64 = (file: File) => new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = () => reject(new Error('Failed to read uploaded file'));
      reader.readAsDataURL(file);
    });
    if (uploadedFile1) uploadedFile1Base64 = await readAsBase64(uploadedFile1);
    if (uploadedFile2) uploadedFile2Base64 = await readAsBase64(uploadedFile2);

    const payload = {
      sourceDocumentIds: selectedDocIds,
      outputFileName: filename.trim(),
      ...(entityId != null ? { entityId } : {}),
      ...(jobId != null ? { jobId } : {}),
      ...(uploadedFile1Base64 ? { uploadedFile1Base64 } : {}),
      ...(uploadedFile2Base64 ? { uploadedFile2Base64 } : {}),
    };

    if (onSubmitted && !needsUploads) {
      try {
        const token = await getAccessToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await axios.post<{ id: number }>(
          `${baseApiUrl}/api/dispatches`,
          { ...payload, promptId: prompt.prompt_id },
          { headers },
        );
        setSubmitting(false);
        onClose();
        onSubmitted(data.id);
      } catch (err) {
        setError(apiError(err));
        setSubmitting(false);
        setStatusMessage('');
        setStatusStartTime(null);
        setElapsedSeconds(0);
      }
      return;
    }

    abortRef.current = new AbortController();

    try {
      const { data } = await axios.post<GenerateResponse>(
        `${baseApiUrl}/api/prompts/${prompt.prompt_id}/generate`,
        payload,
        { signal: abortRef.current.signal },
      );
      onSuccess({ fileName: data.fileName, sharePointUrl: data.sharePointUrl });
    } catch (err) {
      if (!axios.isCancel(err)) {
        setError(apiError(err));
      }
    } finally {
      abortRef.current = null;
      setSubmitting(false);
      setStatusMessage('');
      setStatusStartTime(null);
      setElapsedSeconds(0);
    }
  }

  function handleCancel() {
    if (submitting) {
      abortRef.current?.abort();
    } else {
      onClose();
    }
  }

  if (!prompt) return null;

  const placeholderSuffix = prompt.output_filename_prefix
    ? ` (e.g. "${prompt.output_filename_prefix} FY2026 Q1")`
    : '';

  const unindexedSelectedCount = selectedDocIds.filter((id) => unindexedIds.has(id)).length;

  // Step indicator state — 1 = choose sources, 2 = name output, 3 = ready/generating
  const currentStep =
    submitting ? 3 :
    selectedDocIds.length === 0 ? 1 :
    !filename.trim() ? 2 :
    3;

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableBackdropClose
      width={720}
      title={`Generate Document — ${prompt.prompt_name}`}
      footer={
        <>
          <Btn onClick={handleCancel}>
            {submitting ? 'Cancel Generation' : 'Cancel'}
          </Btn>
          <Btn
            primary
            onClick={handleSubmit}
            disabled={submitting || selectedDocIds.length === 0 || !filename.trim()}
            style={{
              background: (submitting || selectedDocIds.length === 0 || !filename.trim())
                ? undefined
                : GRADIENTS.ai,
              border: 'none',
              boxShadow: (submitting || selectedDocIds.length === 0 || !filename.trim())
                ? undefined
                : SHADOWS.brand,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {Icons.sparkle('#fff', 14)}
              {unindexedSelectedCount > 0
                ? `Index & Generate (${unindexedSelectedCount} to index)`
                : 'Generate'}
            </span>
          </Btn>
        </>
      }
    >
      {/* AI accent banner */}
      <div style={{
        background: GRADIENTS.aiSoft,
        borderRadius: 12,
        padding: '14px 16px',
        marginBottom: 18,
        border: `1px solid ${COLORS.purple}22`,
        display: 'flex', alignItems: 'center', gap: 12,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          width: 4, position: 'absolute', left: 0, top: 0, bottom: 0,
          background: GRADIENTS.ai,
        }} />
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: COLORS.white, color: COLORS.purple,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, boxShadow: SHADOWS.sm,
        }}>
          <SparkleBadge size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.dark }}>
            Claude will draft this document for you
          </div>
          <div style={{ fontSize: 12, color: COLORS.mid, marginTop: 2 }}>
            Pick your source documents, give the output a name, then sit back while Claude reads, analyses and drafts.
          </div>
        </div>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
        {[
          { n: 1, label: 'Choose sources' },
          { n: 2, label: 'Name output' },
          { n: 3, label: 'Generate' },
        ].map((s, i, arr) => {
          const isActive = currentStep === s.n;
          const isDone = currentStep > s.n;
          const color = isDone ? COLORS.green : isActive ? COLORS.purple : COLORS.mid;
          const bg    = isDone ? COLORS.greenSoft : isActive ? COLORS.purpleSoft : COLORS.light;
          return (
            <React.Fragment key={s.n}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: bg, color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700,
                  border: `1.5px solid ${isActive ? color : 'transparent'}`,
                }}>
                  {isDone ? Icons.check(color) : s.n}
                </div>
                <span style={{
                  fontSize: 12, fontWeight: 600,
                  color: isActive || isDone ? COLORS.dark : COLORS.mid,
                }}>{s.label}</span>
              </div>
              {i < arr.length - 1 && (
                <div style={{ flex: 1, height: 1, background: COLORS.border, minWidth: 16 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <FormField
        label="Output filename"
        required
        hint={`.xlsx will be appended automatically if you don't include it${placeholderSuffix}.`}
      >
        <TextInput
          value={filename}
          onChange={setFilename}
          placeholder={prompt.output_filename_prefix ?? 'Enter a filename'}
          autoFocus
          disabled={submitting}
        />
      </FormField>

      <FormField
        label="Source documents"
        required
        hint={`The generated workbook will be saved to the SharePoint folder of the first selected document. ${selectedDocIds.length} selected.`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          {!isAtRoot && (
            <Btn small onClick={navigateBack} disabled={submitting}>
              ← Back
            </Btn>
          )}
          {isAtRoot ? (
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search clients…"
              disabled={submitting}
              style={{
                flex: 1, height: 32, padding: '0 10px', fontSize: 13,
                border: `1px solid ${COLORS.border}`, borderRadius: 6,
                outline: 'none', color: COLORS.dark, background: COLORS.white,
              }}
            />
          ) : (
            <span style={{ fontSize: 12, color: COLORS.mid }}>
              Folder: {folderName || folderId}
            </span>
          )}
        </div>
        <div style={{ maxHeight: 360, overflowY: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
          <DocumentList
            baseApiUrl={baseApiUrl}
            folderId={folderId}
            onNavigate={(id, name) => navigateInto(id, name)}
            multiSelect
            selectedIds={selectedDocIds}
            onSelectionChange={setSelectedDocIds}
            searchFilter={isAtRoot ? search : undefined}
            onItemsLoaded={handleItemsLoaded}
            unindexedIds={unindexedIds}
          />
        </div>
        {unindexedIds.size > 0 && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#92400e', display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg style={{ width: 14, height: 14, flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Documents marked ⚠ are not yet indexed. Selecting them will trigger indexing before generation.
          </div>
        )}
      </FormField>

      {submitting && statusMessage && (
        <div style={{
          marginTop: 14,
          background: GRADIENTS.aiSoft,
          border: `1px solid ${COLORS.purple}22`,
          borderRadius: 12, padding: '14px 16px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div className="crm-gradient-ai" style={{
            position: 'absolute', left: 0, right: 0, top: 0, height: 2,
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: COLORS.white, color: COLORS.purple,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, boxShadow: SHADOWS.sm,
            }}>
              <SparkleBadge size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="crm-anim-pulseSoft">●</span>
                {statusMessage}
              </div>
              <div style={{ fontSize: 11, color: COLORS.mid, marginTop: 3 }}>
                Elapsed {elapsedSeconds}s · Hang tight — large source sets can take a minute.
              </div>
            </div>
          </div>
        </div>
      )}

      {!!prompt.upload_slot_1_label && (
        <FormField label={prompt.upload_slot_1_label}>
          <ExcelFileSlot file={uploadedFile1} onFile={setUploadedFile1} submitting={submitting} onError={setError} />
        </FormField>
      )}
      {!!prompt.upload_slot_2_label && (
        <FormField label={prompt.upload_slot_2_label}>
          <ExcelFileSlot file={uploadedFile2} onFile={setUploadedFile2} submitting={submitting} onError={setError} />
        </FormField>
      )}
      {error && <div style={{ marginTop: 12 }}><ErrorBlock message={error} /></div>}
    </Modal>
  );
};

const EXCEL_ACCEPT = '.xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12';

const ExcelFileSlot: React.FC<{
  file: File | null;
  onFile: (f: File | null) => void;
  submitting: boolean;
  onError: (msg: string | null) => void;
}> = ({ file, onFile, submitting, onError }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
      border: `1px solid ${COLORS.border}`, background: COLORS.white,
      fontSize: 13, color: COLORS.dark,
      opacity: submitting ? 0.5 : 1, pointerEvents: submitting ? 'none' : 'auto',
    }}>
      Choose file
      <input
        type="file"
        accept={EXCEL_ACCEPT}
        style={{ display: 'none' }}
        disabled={submitting}
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          if (f && !f.name.match(/\.(xlsx|xlsm)$/i)) {
            onError('Only .xlsx and .xlsm files are accepted.');
            return;
          }
          onFile(f);
          onError(null);
        }}
      />
    </label>
    {file ? (
      <span style={{ fontSize: 12, color: COLORS.mid }}>
        {file.name} ({(file.size / 1024).toFixed(0)} KB)
        <button type="button" onClick={() => onFile(null)}
          style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: COLORS.mid, fontSize: 12 }}>
          Remove
        </button>
      </span>
    ) : (
      <span style={{ fontSize: 12, color: COLORS.mid }}>No file chosen</span>
    )}
  </div>
);

export default GenerateDocumentModal;
